/**
 * Custom hook for managing answers in a decision tree questionnaire.
 * Handles answer state management, submission, and navigation prevention.
 */
import { AnsweredQuestionsType } from '@/components/decision-tree/hooks';
import {
  FollowUpType,
  NextSurveyItemType,
  QuestionnaireSummary,
  UseSetupCallbackCB,
} from '@/components/decision-tree/types';
import { Reducer, useCallback, useReducer, useState } from 'react';
import { useBeforeUnload } from 'react-router-dom';
import { optionNoneId } from '../../components/SurveyItem/components/Question/questionTypes';
import {
  AnsweredCurrentQuestion,
  AnsweredQuestionsAnswerType,
  AnswerModel,
  HandleAnswersAction,
  HandleAnswersActions,
  HandleAnswersState,
  iDontKnowAnswer,
  useHandleActionResponse,
} from './types';

export type UseHandleAnswersProps = {
  callback: UseSetupCallbackCB;
  answers: AnsweredQuestionsType;
  initialState: NextSurveyItemType;
};

/**
 * Main hook for handling questionnaire answers
 * @param callback - Function to call when submitting answers
 * @param answers - Previously answered questions
 * @param initialState - Initial survey item state
 */
export const useHandleAnswers = ({
  callback,
  answers,
  initialState,
}: UseHandleAnswersProps): useHandleActionResponse => {
  const [summary, setSummary] = useState<QuestionnaireSummary | null>(null);
  const [result, setResult] = useState<Array<FollowUpType> | null>(null);

  /**
   * Reducer to manage answer state with different actions:
   * - SaveAnswer: Stores a new answer with timestamp
   * - ResetAnswer: Clears current answer
   * - SetPreviousAnswers: Updates stored previous answers
   * - GetPreviousAnswer: Retrieves a previously stored answer
   */
  const handleAnswersReducer = (state: HandleAnswersState, { type, payload }: HandleAnswersAction) => {
    switch (type) {
      case HandleAnswersActions.SaveAnswer: {
        const { answer, surveyItemId, sectionId, complete } = payload as AnsweredCurrentQuestion;
        return {
          ...state,
          answer,
          isAnswered: isAnswerFilled(answer),
          complete: complete ?? false,
          previousAnswers: {
            ...state.previousAnswers,
            [surveyItemId]: {
              surveyItemId,
              sectionId,
              answer,
              answeredAt: new Date().toISOString(),
            },
          },
        };
      }
      case HandleAnswersActions.ResetAnswer:
        return { ...state, answer: null, isAnswered: false, complete: false };
      case HandleAnswersActions.SetPreviousAnswers: {
        const newAnswers = (payload as AnsweredQuestionsType).reduce<AnsweredQuestionsAnswerType>((acc, answer) => {
          acc[answer.surveyItemId] = answer;
          return acc;
        }, {});
        const previousAnswers = { ...state.previousAnswers, ...newAnswers };
        return { ...state, previousAnswers };
      }
      case HandleAnswersActions.GetPreviousAnswer: {
        const surveyItemId = payload as string;
        const previousAnswer = state.previousAnswers[surveyItemId];

        return {
          ...state,
          answer: previousAnswer?.answer,
          isAnswered: isAnswerFilled(previousAnswer?.answer),
        };
      }
    }
  };

  // Initialize state with current answer if it exists
  const currentAnswer = answers.find((answer) => answer.surveyItemId === initialState.surveyItemId) ?? null;

  // Create a map of all previous answers for quick lookup
  const answersMap = answers.reduce<AnsweredQuestionsAnswerType>((acc, answer) => {
    acc[answer.surveyItemId] = answer;
    return acc;
  }, {});

  const firstState = currentAnswer?.answer
    ? {
        answer: currentAnswer?.answer,
        previousAnswers: answersMap, // Initialize with all answers instead of empty object
        isAnswered: isAnswerFilled(currentAnswer?.answer),
        complete: false,
      }
    : {
        answer: null,
        previousAnswers: answersMap, // Initialize with all answers instead of empty object
        isAnswered: false,
        complete: false,
      };

  const [state, dispatch] = useReducer<Reducer<HandleAnswersState, HandleAnswersAction>>(
    handleAnswersReducer,
    firstState
  );

  /**
   * Submits the current answer for a survey item
   * Filters out "None" options for multiple choice questions before submission
   */
  const submitAnswer = async (surveyItemId: string) => {
    const finalAnswer = filterMultipleChoiceNoneAnswers(state.answer);
    const response = await callback(surveyItemId, finalAnswer as AnswerModel);
    if (response && response.summary) setSummary(response.summary);
    if (response && response.result) setResult(response.result);
    return response;
  };

  /**
   * Submits an "I don't know" answer for a survey item
   */
  const submitIDKAnswer = async (surveyItemId: string) => {
    const response = await callback(surveyItemId, iDontKnowAnswer);
    if (response && response.summary) setSummary(response.summary);
    if (response && response.result) setResult(response.result);
    return response;
  };

  // Prevent navigation when there's an unanswered question
  useBeforeUnload(
    useCallback(
      (event) => {
        if (state.isAnswered) {
          event.preventDefault();
        }
      },
      [state]
    )
  );

  return { state, answersDispatch: dispatch, submitAnswer, submitIDKAnswer, summary, result };
};

/**
 * Filters out "None" options from multiple choice answers
 * Returns the original answer for non-multiple choice types
 */
const filterMultipleChoiceNoneAnswers = (answer: AnswerModel | null): AnswerModel | null => {
  if (!answer || answer.type !== 'multipleChoice') return answer;

  return { ...answer, values: answer.values.filter((value) => value !== optionNoneId) };
};

/**
 * Checks if an answer is properly filled based on its type
 * Different answer types have different validation rules
 */
const isAnswerFilled = (answer: AnswerModel | undefined): boolean => {
  if (!answer) return false;

  switch (answer.type) {
    case 'singleChoice':
    case 'date':
    case 'email':
    case 'freetext':
      return !!answer.value;
    case 'number':
      return answer.value != null;
    case 'multipleChoice':
      return answer.values?.length > 0;
    case 'height':
      return answer.feet != null || answer.inches != null;
    case 'weight':
      return answer.pounds != null || answer.ounces != null;
    case 'summary':
    case 'result':
    case 'interstitial':
      return true;
  }
  return false;
};
